// src/frontend_react/src/pages/UserDashboard/WelcomePage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import Card from '../../components/ui/Card/Card';
import Typography from '../../components/ui/Typography/Typography';
import Stack from '../../components/ui/Stack/Stack';
import Button from '../../components/ui/Button/Button';
import Icon from '../../components/ui/Icon/Icon';
import Box from '../../components/ui/Box/Box';
import { FiHome, FiLock, FiLink, FiUploadCloud } from 'react-icons/fi';

import styles from './WelcomePage.module.css';

const WelcomePage = () => {
  const navigate = useNavigate();
  const { user, hasRole, getImpliedSourceType } = useAuth();

  const isDloOrAdmin =
    hasRole('system_admin') || hasRole('data_linkage_officer');
  const stewardSourceType = getImpliedSourceType();

  const renderDloAdminWelcome = () => (
    <Card
      title={`Welcome to CIPHER, ${user?.first_name || user?.username}!`}
      titleIcon={<Icon as={FiHome} />}
    >
      <Stack spacing="lg">
        <Typography color="secondary">
          This platform empowers you to handle sensitive data with confidence,
          ensuring every step is secure, compliant, and aligned with best
          practices.
        </Typography>
        <Stack as="ul" spacing="md" className={styles.capabilitiesList}>
          <Stack as="li" direction="row" spacing="md" alignItems="flex-start">
            <Icon as={FiUploadCloud} color="primary" mt={0.5} />
            <Typography>
              <strong>Securely Prepare</strong> datasets for privacy-preserving
              linkage. Sensitive personal data (PII) remains on local machines,
              deidentified and encrypted before registration.
            </Typography>
          </Stack>
          <Stack as="li" direction="row" spacing="md" alignItems="flex-start">
            <Icon as={FiLink} color="primary" mt={0.5} />
            <Typography>
              <strong>Initiate and Oversee</strong> record linkage processes
              that combine data from multiple sources without ever exposing
              sensitive details.
            </Typography>
          </Stack>
          <Stack as="li" direction="row" spacing="md" alignItems="flex-start">
            <Icon as={FiLock} color="primary" mt={0.5} />
            <Typography>
              <strong>Access and Decrypt</strong> processed outputs, including
              securely linked datasets and detailed reports for your review,
              analysis, and further action.
            </Typography>
          </Stack>
        </Stack>
        <Box mt="xl">
          <Button onClick={() => navigate('/user/workspace')}>
            Go to Project Workspace
          </Button>
        </Box>
      </Stack>
    </Card>
  );

  const renderStewardWelcome = () => (
    <Card
      title={`Welcome, ${user?.first_name || user?.username}!`}
      titleIcon={<Icon as={FiHome} />}
    >
      <Stack spacing="lg">
        <Typography variant="h4" as="h2">
          Your Role: Data Steward for {stewardSourceType}
        </Typography>
        <Typography color="secondary">
          Your role is to prepare and contribute the{' '}
          <strong>{stewardSourceType}</strong> dataset. This process ensures
          your sensitive data is deidentified and encrypted on your machine
          before it's registered with the central platform.
        </Typography>
        <Box mt="xl">
          <Button onClick={() => navigate('/user/workspace')}>
            Go to My Data Contribution Workspace
          </Button>
        </Box>
      </Stack>
    </Card>
  );

  return (
    <Stack spacing="xl" alignItems="center">
      <Box width="100%" maxWidth="900px" className={styles.welcomeCard}>
        {isDloOrAdmin ? renderDloAdminWelcome() : renderStewardWelcome()}
      </Box>
    </Stack>
  );
};

export default WelcomePage;
